import { z } from 'zod';
import type { FunnelStage } from './api-funnel.js';

// v1 Meta Conversions API (spec §08.6.1): commercial events sent to the developer's own dataset only when the real
// transition happened (funnel stage change backed by evidence — a held visit only when the outcome was reported).
// Deterministic event ids (opportunity + stage) prevent duplicates; acceptance / errors are recorded per event.

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const CAPI_STAGES = ['assigned', 'contacted', 'conversation_open', 'qualified', 'appointment_scheduled', 'appointment_completed'] as const satisfies readonly FunnelStage[];
export type CapiStage = (typeof CAPI_STAGES)[number];

/** Defaults: Meta standard names where one fits; «Lead» off (Meta lead forms already report it — avoid double counting). */
export const CAPI_DEFAULT_EVENTS: Record<CapiStage, { enabled: boolean; eventName: string }> = {
  assigned: { enabled: false, eventName: 'Lead' },
  contacted: { enabled: true, eventName: 'Contact' },
  conversation_open: { enabled: true, eventName: 'ConversacionIniciada' },
  qualified: { enabled: true, eventName: 'LeadCalificado' },
  appointment_scheduled: { enabled: true, eventName: 'Schedule' },
  appointment_completed: { enabled: true, eventName: 'VisitaRealizada' },
};
export const CAPI_STAGE_LABELS: Record<CapiStage, string> = {
  assigned: 'Lead recibido',
  contacted: 'Contacto efectivo',
  conversation_open: 'Conversación iniciada',
  qualified: 'Lead calificado',
  appointment_scheduled: 'Cita agendada',
  appointment_completed: 'Cita realizada (resultado confirmado)',
};

const EventName = z.string().trim().regex(/^[A-Za-z][A-Za-z0-9_]{1,49}$/, 'Usa letras, números y guion bajo (máx. 50)');
export const CapiEventMappingSchema = z.object({ enabled: z.boolean(), eventName: EventName });

export const MetaCapiSettingsSchema = z.object({
  enabled: z.boolean(),
  datasetId: z.string().nullable(),
  /** The token is never returned; only whether one is stored. */
  hasToken: z.boolean(),
  testEventCode: z.string().nullable(),
  events: z.record(z.enum(CAPI_STAGES), CapiEventMappingSchema),
  projectDatasets: z.record(z.string(), z.string()),
  verifiedAt: Iso.nullable(),
  lastError: z.string().nullable(),
});
export type MetaCapiSettings = z.infer<typeof MetaCapiSettingsSchema>;

export const PutMetaCapiSettingsSchema = z.object({
  enabled: z.boolean(),
  datasetId: z.string().trim().regex(/^\d{5,20}$/, 'El id del dataset es numérico').nullable(),
  /** Write-only. Omit to keep the stored token; null removes it. */
  accessToken: z.string().trim().min(20).max(1000).nullable().optional(),
  testEventCode: z.string().trim().max(40).nullable().optional(),
  events: z.record(z.enum(CAPI_STAGES), CapiEventMappingSchema).optional(),
  projectDatasets: z.record(Id, z.string().trim().regex(/^\d{5,20}$/)).optional(),
});
export type PutMetaCapiSettings = z.infer<typeof PutMetaCapiSettingsSchema>;

export const MetaCapiEventSchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  leadInterestId: Id,
  contactName: z.string().nullable(),
  stage: z.string(),
  eventName: z.string(),
  eventId: z.string(),
  status: z.enum(['pending', 'sent', 'failed', 'skipped']),
  attempts: z.number().int(),
  detail: z.string().nullable(),
  matchKeys: z.array(z.string()),
  eventTime: Iso,
  sentAt: Iso.nullable(),
});
export type MetaCapiEvent = z.infer<typeof MetaCapiEventSchema>;

export const MetaCapiTestResultSchema = z.object({ ok: z.boolean(), detail: z.string(), eventsReceived: z.number().int().nullable() });
